import { NAVIGATION_ROUTES } from '@/routes/constants';
import { useGetPendingOrder } from '@/services/dashboard';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import {
  Card,
  Flex,
  Text,
  VStack,
  Spinner,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useMoveStatus } from '@/services/OrderManagement';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import CheckIcon from '@/assets/svg/check-stroke.svg?react';
import eyeIcon from '@/assets/svg/eye.svg?react';
import StatusBadge from '@/components/common/StatusBadge';
import OrderDetails from '@/pages/AllOrders/OrderDetail';
import CustomizedIcon from '@/assets/svg/customizable-icon.svg?react';
import CustomizationDetailsModal from '@/components/common/CustomizationDetailsModal';
import { Order } from '@/services/OrderManagement/types'; // Import full Order type

interface PendingOrderItem {
  order_id: string;
  quantity: number;
  amount: string;
  currency: string;
  status: string;
  sku?: string | null;
  prep_days_remaining: number;
  is_customized?: boolean;
  // Add other fields if needed for compatibility with full Order
}

const BACKEND_STATUS_MAP: Record<string, string> = {
  'Order Confirmed': 'not_yet_shipped',
  'Under Maintainance': 'under_manufacture',
  'Ready For Delivery': 'ready_for_delivery',
};

const STATUS_FLOW = {
  not_yet_shipped: 'not_yet_shipped',
  under_manufacture: 'under_manufacture',
  ready_for_delivery: 'ready_for_delivery',
} as const;

type StatusKey = keyof typeof STATUS_FLOW;

const STATUS_LABEL_TO_API: Record<StatusKey, string> = {
  not_yet_shipped: 'Order Confirmed',
  under_manufacture: 'Under Maintainance', //manufacture
  ready_for_delivery: 'Ready for Delivery',
};

const DISPLAY_LABEL: Record<StatusKey, string> = {
  not_yet_shipped: 'Not Yet Shipped',
  under_manufacture: 'Under Manufacture',
  ready_for_delivery: 'Ready for Delivery',
};

const getCurrentStatus = (apiStatus: string | undefined): StatusKey => {
  if (!apiStatus) return STATUS_FLOW.not_yet_shipped;
  const mapped = BACKEND_STATUS_MAP[apiStatus];
  if (mapped) return mapped as StatusKey;
  const lower = apiStatus.toLowerCase();
  if (lower.includes('order confirmed')) return STATUS_FLOW.not_yet_shipped;
  if (lower.includes('maintainance') || lower.includes('manufactur'))
    return STATUS_FLOW.under_manufacture;
  if (lower.includes('ready')) return STATUS_FLOW.ready_for_delivery;
  return STATUS_FLOW.not_yet_shipped;
};

const getNextValidStatus = (current: StatusKey, isCustomized: boolean): StatusKey | null => {
  if (current === STATUS_FLOW.ready_for_delivery) return null;

  if (isCustomized) {
    if (current === STATUS_FLOW.not_yet_shipped) return STATUS_FLOW.under_manufacture;
    if (current === STATUS_FLOW.under_manufacture) return STATUS_FLOW.ready_for_delivery;
  } else {
    if (current === STATUS_FLOW.not_yet_shipped) return STATUS_FLOW.ready_for_delivery;
  }

  return null;
};

const PendingOrder = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetPendingOrder() as {
    data: PendingOrderItem[] | undefined;
    isLoading: boolean;
  };

  const moveStatusMutation = useMoveStatus();

  const [selectedOrderId, setSelectedOrderId] = React.useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState<{
    isOpen: boolean;
    orderId: string;
    newStatus: StatusKey;
    targetLabel: string;
  } | null>(null);
  const [selectedOrder, setSelectedOrder] = React.useState<Order | null>(null);
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = React.useState(false);

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const openCustomizationModal = async (orderId: string) => {
    // Fetch full order details here if needed; assuming useGetPendingOrder provides enough,
    // but to match AllOrder, you may need a query to fetch full Order by ID
    // For now, placeholder: setSelectedOrder(fullOrderFromQuery)
    // If PendingOrderItem has all fields, cast it
    const item = data?.find((o: PendingOrderItem) => o.order_id === orderId);
    if (item) {
      // Cast to Order assuming fields overlap; in production, fetch full
      setSelectedOrder(item as unknown as Order);
    }
    setIsCustomizationModalOpen(true);
  };

  const handleMoveStatusClick = (order: PendingOrderItem) => {
    const currentStatus = getCurrentStatus(order.status);
    const nextStatus = getNextValidStatus(currentStatus, order.is_customized === true);

    if (!nextStatus) return;

    const targetLabel = DISPLAY_LABEL[nextStatus];

    setConfirmModal({
      isOpen: true,
      orderId: order.order_id,
      newStatus: nextStatus,
      targetLabel,
    });
  };

  const handleConfirmStatusChange = async () => {
    if (!confirmModal) return;

    const { orderId, newStatus } = confirmModal;
    const newStatusString = STATUS_LABEL_TO_API[newStatus];

    try {
      await moveStatusMutation.mutateAsync({
        orderId,
        newStatus: newStatusString,
      });

      await queryClient.invalidateQueries({ queryKey: ['pendingOrder'] });
      await queryClient.invalidateQueries({ queryKey: ['orderStatusCounts'] });
    } catch (error) {
      // Optionally show error toast
      console.error('Failed to update status:', error);
    } finally {
      setConfirmModal(null);
    }
  };

  const orders = data?.slice(0, 5) ?? [];
  const rowCount = orders.length;

  return (
    <>
      <Card
        shadow="none"
        h={430}
        borderColor="border.default60"
        fontFamily="primary"
        borderWidth="1px"
        borderRadius="8px"
        overflow="hidden"
      >
        <Flex justify="space-between" align="center" p={4}>
          <Text fontSize="xl" fontWeight="semibold">
            Pending Orders
          </Text>
          <Box
            as={Link}
            to={NAVIGATION_ROUTES.ALL_ORDERS}
            state={{ tabIndex: 1 }} // <-- ADDED: Navigates to Pending Orders tab (index 1)
            color="brand.primary.DEFAULT"
            fontWeight="semibold"
            fontSize="md"
            _hover={{ textDecoration: 'underline' }}
            _focusVisible={{
              bg: 'brand.primary.50',
              outline: '2px solid',
              outlineColor: 'brand.primary.DEFAULT',
              borderRadius: '4px',
            }}
          >
            View All
          </Box>
        </Flex>

        {isLoading ? (
          <VStack h="360px" justify="center">
            <Spinner size="lg" color="brand.primary.DEFAULT" />
          </VStack>
        ) : rowCount === 0 ? (
          <VStack h="360px" justify="center" spacing={2}>
            <Text fontSize="xl" fontWeight="semibold" color="text.primary">
              No pending orders
            </Text>
            <Text fontSize="sm" color="text.disabled">
              All orders are processed or delivered.
            </Text>
          </VStack>
        ) : (
          <TableContainer
            h="340px"
            overflowX="auto"
            overflowY="hidden"
            css={{
              '&::-webkit-scrollbar': { display: 'none' },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none',
            }}
          >
            <Table variant="simple" size="sm">
              <Thead bg="background.secondary" position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th minW="100px">Product ID</Th>
                  <Th minW="60px">Qty</Th>
                  <Th minW="80px">Remaining Time</Th>
                  <Th minW="80px">Amount</Th>
                  <Th minW="140px">Status</Th>
                  <Th minW="100px">Action</Th>
                </Tr>
              </Thead>
              <Tbody height="auto">
                {orders.map((order) => {
                  const orderId = order.order_id;
                  const currentStatus = getCurrentStatus(order.status);
                  const displayId = order.sku?.trim() ? order.sku : orderId;
                  const isCustomized = order.is_customized === true;
                  const nextStatus = getNextValidStatus(currentStatus, isCustomized);
                  const canMove = !!nextStatus;
                  return (
                    <Tr key={orderId} height="68px">
                      <Td fontSize="sm" color="text.primary" py={2} whiteSpace="nowrap">
                        <Flex align="center" gap={2}>
                          {displayId}
                          {isCustomized && (
                            <Tooltip
                              label="This product is customized"
                              borderRadius="md"
                              bg="background.primary"
                              color="text.secondary"
                              fontSize="xs"
                              hasArrow
                              placement="bottom"
                            >
                              <Box
                                bg={'#F2F4F8'}
                                borderRadius={'3px'}
                                p={1}
                                cursor="pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCustomizationModal(orderId);
                                }}
                                _hover={{ bg: 'brand.primary.50' }}
                                transition="background 0.2s"
                              >
                                <Box p="2px" display="flex" alignItems="center">
                                  <Icon
                                    as={CustomizedIcon}
                                    color={'brand.primary.DEFAULT'}
                                    boxSize={4}
                                  />
                                </Box>
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>
                      <Td fontSize="sm" py={2} whiteSpace="nowrap">
                        {order.quantity}
                      </Td>
                      <Td
                        fontSize="sm"
                        py={2}
                        whiteSpace="nowrap"
                        fontWeight={'bold'}
                        color={
                          order?.prep_days_remaining != null
                            ? order?.prep_days_remaining <= 0
                              ? 'status.error'
                              : 'status.success'
                            : 'text.disabled'
                        }
                      >
                        {order.prep_days_remaining ?? '-'}
                      </Td>

                      <Td fontSize="sm" py={2} whiteSpace="nowrap">
                        {getCurrencySymbol(order.currency)}
                        {parseFloat(order.amount).toFixed(2)}
                      </Td>
                      <Td py={2}>
                        <StatusBadge status={DISPLAY_LABEL[currentStatus]} width="130px" />
                      </Td>
                      <Td py={2}>
                        <Flex gap={2} align="center" minW="80px">
                          {/* View Icon */}
                          <Tooltip label="View Order" placement="bottom" hasArrow>
                            <Box display="inline-block">
                              <Icon
                                as={eyeIcon}
                                boxSize={5}
                                color="brand.primary.DEFAULT"
                                onClick={() => handleViewOrder(orderId)}
                                cursor="pointer"
                              />
                            </Box>
                          </Tooltip>

                          {/* Move Status Icon */}
                          {canMove && (
                            <Tooltip
                              label={`Move to ${DISPLAY_LABEL[nextStatus!]}`}
                              placement="bottom"
                              hasArrow
                            >
                              <Box display="inline-block">
                                {moveStatusMutation.isLoading ? (
                                  <Spinner
                                    size="sm"
                                    color="brand.primary.DEFAULT"
                                    thickness="2px"
                                  />
                                ) : (
                                  <Icon
                                    as={CheckIcon}
                                    boxSize={5}
                                    color="brand.primary.DEFAULT"
                                    onClick={() => handleMoveStatusClick(order)}
                                    cursor="pointer"
                                  />
                                )}
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Order Details Modal */}
      <Modal size="5xl" isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Order Details</ModalHeader>
          <ModalBody
            maxHeight="70vh"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
            }}
          >
            {selectedOrderId && <OrderDetails id={selectedOrderId} />}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand.primary" onClick={closeModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmation Modal */}
      {confirmModal && (
        <CustomConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={handleConfirmStatusChange}
          icon={CheckIcon}
          title="Confirm Status Change"
          description={`Are you sure you want to move this order to "${confirmModal.targetLabel}"?`}
          buttonText="Confirm"
          isLoading={moveStatusMutation.isLoading}
        />
      )}

      {/* Customization Details Modal */}
      <CustomizationDetailsModal
        isOpen={isCustomizationModalOpen}
        onClose={() => setIsCustomizationModalOpen(false)}
        order={selectedOrder ?? undefined}
      />
    </>
  );
};

export default PendingOrder;
